// Client-side "new upload" tracking for the aircraft Uploads tab.
//
// Uploads use browser-local state on purpose: the NEW tag is dismissed by
// merely mousing over the row, so it is throwaway per-device state and writing
// a database row for every hover would be pure chatter. Unreviewed FLIGHTS are
// different (they persist until the flight's dashboard is opened, and can be
// cleared in bulk), so those live in the database and arrive on the flights
// manifest as `is_new`; nothing in this module touches flights.
//
// Storage shape, per aircraft:
//   alaryiq-uploads-seen:<aircraftId> -> JSON array of "<uploadId>|<uploadedAtMs>"
// (or "<uploadId>|*" for a dismissal with no known timestamp). The timestamp is
// load-bearing, not decoration — see the readSeen comment: upload ids are
// recycled, so a mark has to say WHICH upload event it dismissed.
// Entries are capped so a busy aircraft cannot grow without bound.
//
// Rollout baseline: the first time an aircraft is seen, every upload it
// currently has is recorded as already-seen, so an established account does
// not light up with tags the first time this ships. Only uploads that appear
// after that count as new.

const SEEN_PREFIX = "alaryiq-uploads-seen:";
const BASELINE_KEY = "alaryiq-uploads-baseline";
const SEEN_ID_LIMIT = 500;

// Device-wide "nothing before this instant is new" mark, stamped once on first
// use. It is deliberately NOT per aircraft: a per-aircraft baseline seeded on
// first render swallows uploads that arrived before you first opened that
// aircraft's Uploads tab, which is exactly how a brand new upload ended up
// never being tagged. This mirrors users.flight_new_baseline_at on the flights
// side, which is why flights behaved correctly and uploads did not.
function baselineAt(): number {
  try {
    const stored = globalThis.localStorage?.getItem(BASELINE_KEY);
    const parsed = stored ? Date.parse(stored) : Number.NaN;
    if (Number.isFinite(parsed)) return parsed;
    const now = new Date();
    globalThis.localStorage?.setItem(BASELINE_KEY, now.toISOString());
    return now.getTime();
  } catch {
    // No storage: treat everything as already seen rather than tagging a whole
    // history the user has certainly looked at before.
    return Date.now();
  }
}

function uploadedAfterBaseline(uploadedAt: unknown, baseline: number): boolean {
  const ms = uploadedAtMs(uploadedAt);
  // Undated rows are treated as old: better to miss a tag than to light up a
  // whole history because a timestamp was missing.
  return Number.isFinite(ms) && ms > baseline;
}

function storageKey(aircraftId: string) {
  return `${SEEN_PREFIX}${aircraftId}`;
}

// An upload id is NOT unique over time. It is derived deterministically from
// the aircraft id and the storage key (see stableUuid("upload", ...) in
// createUploadWithFlightsImpl), and the storage key is just the filename under
// the aircraft's folder — it only gains a "-2" suffix while another row still
// holds it. So deleting a log and uploading the same file again regenerates the
// SAME upload id. Recording bare ids as seen therefore made a re-uploaded file
// permanently untaggable on that device: the id was already in the list.
//
// Entries are stored as "<id>|<uploadedAtMs>" so a re-upload, which necessarily
// carries a newer uploaded_at, is not covered by the older mark. A bare legacy
// id (written before this change) is read as "seen at the device baseline",
// which makes it a no-op — anything at or below the baseline is already
// filtered out — so stale marks heal themselves on the next dismissal instead
// of suppressing a genuinely new upload forever.
type SeenMarks = Map<string, number>;

function readSeen(aircraftId: string, baseline: number): SeenMarks {
  const marks: SeenMarks = new Map();
  if (!aircraftId) return marks;
  try {
    const raw = globalThis.localStorage?.getItem(storageKey(aircraftId));
    if (!raw) return marks;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return marks;
    for (const entry of parsed) {
      if (typeof entry !== "string" || !entry) continue;
      const split = entry.lastIndexOf("|");
      const id = split === -1 ? entry : entry.slice(0, split);
      if (!id) continue;
      const suffix = split === -1 ? "" : entry.slice(split + 1);
      // "*" means dismissed regardless of timestamp (an id handed to us with no
      // uploaded_at); a bare legacy id reads as the baseline, i.e. a no-op.
      const stamp = suffix === "*" ? Number.POSITIVE_INFINITY : Number(suffix);
      const at = suffix === "*" ? Number.POSITIVE_INFINITY : (Number.isFinite(stamp) && suffix ? stamp : baseline);
      // Keep the newest mark if an id somehow appears twice.
      marks.set(id, Math.max(marks.get(id) ?? Number.NEGATIVE_INFINITY, at));
    }
    return marks;
  } catch {
    return new Map();
  }
}

function writeSeen(aircraftId: string, marks: SeenMarks) {
  if (!aircraftId) return;
  try {
    // Keep the most recently added ids; insertion order is preserved by Map.
    const trimmed = [...marks]
      .slice(-SEEN_ID_LIMIT)
      .map(([id, at]) => `${id}|${Number.isFinite(at) ? at : "*"}`);
    globalThis.localStorage?.setItem(storageKey(aircraftId), JSON.stringify(trimmed));
  } catch {
    /* storage unavailable (private mode, quota): degrade to nothing is new */
  }
}

/** Has this exact upload EVENT been dismissed? A newer row under a recycled id has not. */
function markedSeen(marks: SeenMarks, id: string, uploadedAtMs: number): boolean {
  const at = marks.get(id);
  return at !== undefined && at >= uploadedAtMs;
}

function uploadedAtMs(uploadedAt: unknown): number {
  return typeof uploadedAt === "number" ? uploadedAt : Date.parse(String(uploadedAt ?? ""));
}

export type UploadRef = { id: string; uploadedAt?: unknown };

/**
 * Which of these uploads this device has not seen: arrived after the device
 * baseline, and not yet dismissed. Works the first time an aircraft is opened,
 * because the baseline is a timestamp rather than "whatever was present when
 * I first looked at this aircraft".
 */
export function newUploadIds(aircraftId: string, uploads: UploadRef[] = []): Set<string> {
  if (!aircraftId) return new Set();
  const baseline = baselineAt();
  const seen = readSeen(aircraftId, baseline);
  const pending: SeenMarks = new Map();
  for (const upload of uploads) {
    if (!upload?.id) continue;
    const id = String(upload.id);
    const at = uploadedAtMs(upload.uploadedAt);
    if (!uploadedAfterBaseline(upload.uploadedAt, baseline)) continue;
    if (markedSeen(seen, id, at)) continue;
    pending.set(id, at);
  }
  // Remember what is currently tagged (and when each landed) so leaving the tab
  // can clear exactly those, without the caller having to hand them back.
  pendingNewUploads = { aircraftId, marks: pending };
  return new Set(pending.keys());
}

// What the Uploads table most recently rendered as new, so the navigate-away
// dismissal knows what to clear.
let pendingNewUploads: { aircraftId: string; marks: SeenMarks } = { aircraftId: "", marks: new Map() };

/**
 * Second dismissal path for uploads: leaving the Uploads tab marks everything
 * currently tagged as seen. (The first is mousing over a row.) Safe to call on
 * every panel change; a no-op when nothing is tagged.
 */
export function clearUploadsNewOnLeave() {
  const { aircraftId, marks } = pendingNewUploads;
  if (!aircraftId || !marks.size) return;
  markUploadsSeen(aircraftId, [...marks].map(([id, uploadedAt]) => ({ id, uploadedAt })));
  pendingNewUploads = { aircraftId, marks: new Map() };
  setAircraftUploadsHaveNew(aircraftId, false);
}

/**
 * Does this aircraft have unseen uploads? Read-only on purpose: unlike
 * newUploadIds it never seeds a baseline and never touches the pending set, so
 * it is safe to call from panels other than Uploads. That matters because the
 * Uploads panel only renders while its own tab is showing, and the tab dot has
 * to appear the moment an upload lands, whatever tab you are on.
 *
 * Returns false when no baseline exists yet, leaving the seeding to the
 * Uploads panel; guessing here could mark a whole history as unseen.
 */
export function uploadsHaveNew(aircraftId: string, uploads: UploadRef[] = []): boolean {
  if (!aircraftId) return false;
  const baseline = baselineAt();
  const seen = readSeen(aircraftId, baseline);
  return uploads.some((upload) => upload?.id
    && uploadedAfterBaseline(upload.uploadedAt, baseline)
    && !markedSeen(seen, String(upload.id), uploadedAtMs(upload.uploadedAt)));
}

/**
 * Record uploads as seen. Returns true when something actually changed.
 * Accepts bare ids for convenience; those are stamped at the current instant,
 * which is correct for a dismissal happening now.
 */
export function markUploadsSeen(
  aircraftId: string,
  uploads: Array<string | { id: string; uploadedAt?: unknown }> = [],
): boolean {
  if (!aircraftId || !uploads.length) return false;
  const baseline = baselineAt();
  const seen = readSeen(aircraftId, baseline);
  let changed = false;
  for (const entry of uploads) {
    const id = typeof entry === "string" ? entry : String(entry?.id ?? "");
    if (!id) continue;
    // No usable timestamp (a bare id): dismiss it unconditionally rather than
    // stamping "now", which would make repeat calls non-idempotent.
    const raw = typeof entry === "string" ? Number.NaN : uploadedAtMs(entry?.uploadedAt);
    const at = Number.isFinite(raw) ? raw : Number.POSITIVE_INFINITY;
    if (markedSeen(seen, id, at)) continue;
    seen.set(id, at);
    changed = true;
  }
  if (!changed) return false;
  writeSeen(aircraftId, seen);
  return true;
}

/**
 * Hover dismissal: mousing over a tagged row clears just that upload. Returns
 * true when the tag should be removed from the DOM now.
 */
export function dismissUploadOnHover(aircraftId: string, uploadId: string) {
  if (!aircraftId || !uploadId || !pendingNewUploads.marks.has(uploadId)) return false;
  markUploadsSeen(aircraftId, [{ id: uploadId, uploadedAt: pendingNewUploads.marks.get(uploadId) }]);
  pendingNewUploads.marks.delete(uploadId);
  setAircraftUploadsHaveNew(aircraftId, pendingNewUploads.marks.size > 0);
  return true;
}

/** Test seam: forget this device's state for one aircraft. */
export function resetUploadSeenState(aircraftId: string) {
  try {
    globalThis.localStorage?.removeItem(storageKey(aircraftId));
  } catch {
    /* nothing to reset */
  }
}

// --- Unviewed AI summaries --------------------------------------------------
// Purple "New" pill on the Flights table's AI Summary button until this device
// opens that summary. Client-side like uploads: viewing is a this-device
// action, and each regeneration mints a fresh aiSummaryId, so storing
// flightId -> last-viewed aiSummaryId is enough — a regenerated summary has a
// different id and the pill returns on its own.
//
// Rollout baseline: reuses the device-wide baselineAt() timestamp. Summaries
// generated before this device's baseline are never "new", so an established
// account does not light up every historical summary the first time this ships.

const AI_SUMMARY_SEEN_KEY = "alaryiq-ai-summary-seen";
const AI_SUMMARY_SEEN_LIMIT = 800;

function readAiSummarySeen(): Record<string, string> {
  try {
    const raw = globalThis.localStorage?.getItem(AI_SUMMARY_SEEN_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Is this flight's current AI summary unviewed on this device? True only when
 * a summary exists, it was generated after the device baseline, and this
 * device has not opened that exact summary id.
 */
export function aiSummaryIsNew(flightId: string, aiSummaryId: string, generatedAt?: unknown): boolean {
  if (!flightId || !aiSummaryId) return false;
  const generatedMs = uploadedAtMs(generatedAt);
  // Undated summaries are treated as old, mirroring uploads: better to miss a
  // pill than to tag a whole history.
  if (!Number.isFinite(generatedMs) || generatedMs <= baselineAt()) return false;
  return readAiSummarySeen()[flightId] !== String(aiSummaryId);
}

/** Record that this device opened the flight's current AI summary. */
export function markAiSummarySeen(flightId: string, aiSummaryId: string) {
  if (!flightId || !aiSummaryId) return;
  try {
    const seen = readAiSummarySeen();
    if (seen[flightId] === String(aiSummaryId)) return;
    seen[flightId] = String(aiSummaryId);
    const entries = Object.entries(seen).slice(-AI_SUMMARY_SEEN_LIMIT);
    globalThis.localStorage?.setItem(AI_SUMMARY_SEEN_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    /* storage unavailable: degrade to the pill persisting on this device */
  }
}

// --- Aircraft tab dots ------------------------------------------------------
// A small dot on the Uploads / Flights tabs when that tab holds something the
// user has not looked at. The two panels compute their own "has new" from
// different sources (uploads from localStorage above, flights from the
// server-decorated manifest), so each pushes its flag in and the writer paints
// whatever the latest pair says.
//
// Scoped to #aircraftPageTabs on purpose: the topbar section nav carries a
// second set of [data-aircraft-panel] buttons, and badging both would double
// up the indicator.

const tabHasNew = { uploads: false, flights: false };

function paintTabDots() {
  // Node test harnesses render these modules against minimal DOM stubs, so
  // every hop is feature-checked rather than assumed (same defensive shape as
  // the workspace renderer's cache access). A stub that cannot paint simply
  // no-ops; the dot is decorative.
  const doc = globalThis.document as Document | undefined;
  if (typeof doc?.querySelector !== "function" || typeof doc.createElement !== "function") return;
  const strip = doc.querySelector("#aircraftPageTabs");
  if (!strip || typeof strip.querySelector !== "function") return;
  for (const panel of ["uploads", "flights"] as const) {
    const tab = strip.querySelector(`[data-aircraft-panel="${panel}"]`);
    if (!tab || typeof tab.querySelector !== "function" || typeof tab.appendChild !== "function") continue;
    const existing = tab.querySelector(".tab-new-dot");
    if (tabHasNew[panel]) {
      if (!existing) {
        const dot = doc.createElement("span");
        dot.className = "tab-new-dot";
        // Decorative: the tab's own label already names the section, and the
        // per-row New tags carry the meaning for assistive tech.
        dot.setAttribute?.("aria-hidden", "true");
        tab.appendChild(dot);
      }
    } else if (typeof existing?.remove === "function") {
      existing.remove();
    }
  }
}

export function setAircraftUploadsHaveNew(_aircraftId: string, hasNew: boolean) {
  tabHasNew.uploads = Boolean(hasNew);
  paintTabDots();
}

export function setAircraftFlightsHaveNew(hasNew: boolean) {
  tabHasNew.flights = Boolean(hasNew);
  paintTabDots();
}

/** Repaint after the tab strip is rebuilt or revealed. */
export function syncAircraftTabNewDots() {
  paintTabDots();
}
